import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppStore, selectOrderedOptions } from '../store/useAppStore';
import { useRequireQuestionAccessible } from '../hooks/useRouteGuards';
import { QUESTION_IDS } from '../types/ids';
import { AnswerOptionGroup } from '../components/ui/AnswerOptionGroup';
import { QuestionHelp } from '../components/test/QuestionHelp';
import { Button } from '../components/ui/Button';
import { ProgressBar } from '../components/navigation/ProgressBar';
import { NotFoundPage } from './NotFoundPage';
import { trackEvent } from '../utils/posthogClient';

export function TestQuestionPage() {
  const params = useParams<{ number: string }>();
  const questionNumber = Number(params.number);
  const isValidNumber =
    Number.isInteger(questionNumber) &&
    questionNumber >= 1 &&
    questionNumber <= QUESTION_IDS.length;

  const ready = useRequireQuestionAccessible(isValidNumber ? questionNumber : 1);
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromReview = (location.state as { from?: string } | null)?.from === 'review';
  const dataModel = useAppStore((state) => state.dataModel);
  const session = useAppStore((state) => state.session);
  const setAnswer = useAppStore((state) => state.setAnswer);
  const setCurrentQuestionIndex = useAppStore((state) => state.setCurrentQuestionIndex);
  const reviewStepEnabled = useAppStore((state) => state.settings.featureFlags.reviewStep.enabled);
  const analyticsEnabled = useAppStore((state) => state.settings.featureFlags.analytics.enabled);

  useEffect(() => {
    if (ready && isValidNumber) setCurrentQuestionIndex(questionNumber - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, isValidNumber, questionNumber]);

  if (!isValidNumber) {
    return <NotFoundPage />;
  }
  if (!ready || !session) return null;

  const questionId = QUESTION_IDS[questionNumber - 1];
  if (!questionId) return <NotFoundPage />;
  const question = dataModel.questions.find((q) => q.id === questionId);
  if (!question) return <NotFoundPage />;

  const orderedOptions = selectOrderedOptions(session, questionId, dataModel);
  const currentAnswer = session.answers[questionId];

  function goToPrevious(): void {
    if (analyticsEnabled) trackEvent('question_back', { question_number: questionNumber });
    if (questionNumber === 1) {
      void navigate('/kontext/arbeitsweise');
    } else {
      void navigate(`/test/${questionNumber - 1}`);
    }
  }

  function goToNext(): void {
    if (analyticsEnabled && currentAnswer) {
      trackEvent('question_completed', {
        question_number: questionNumber,
        question_id: currentAnswer.questionId,
        option_id: currentAnswer.optionId,
        score: currentAnswer.score,
      });
    }
    if (cameFromReview || questionNumber === QUESTION_IDS.length) {
      void navigate(reviewStepEnabled ? '/pruefen' : '/ergebnis');
    } else {
      void navigate(`/test/${questionNumber + 1}`);
    }
  }

  return (
    <div className="page-container stack">
      <ProgressBar
        current={questionNumber}
        total={QUESTION_IDS.length}
        label={`Frage ${questionNumber} von ${QUESTION_IDS.length}`}
      />

      <div
        className="stack"
        style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <div>
          <h1>{question.title}</h1>
          <p>{question.question}</p>
          <p>
            <em>{question.shortHint}</em>
          </p>
        </div>
        <QuestionHelp question={question} />
      </div>

      <AnswerOptionGroup
        legend={question.question}
        name={question.id}
        options={orderedOptions.map((option) => ({ id: option.id, label: option.label }))}
        selectedId={currentAnswer?.optionId}
        onSelect={(optionId) => setAnswer(question.id, optionId)}
      />

      <div
        className="test-actions stack"
        style={{ flexDirection: 'row', justifyContent: 'space-between' }}
      >
        <Button variant="secondary" onClick={goToPrevious}>
          Zurück
        </Button>
        <Button onClick={goToNext} disabled={!currentAnswer}>
          Weiter
        </Button>
      </div>
    </div>
  );
}
